import React, { useState } from 'react';
import { Badge, Card, Col, Form, Row } from 'react-bootstrap';
import MediaCapture from './MediaCapture';
import MediaModal from './MediaModal';
import MediaPreview from './MediaPreview';

const BASE_URL = process.env.REACT_APP_API_URL;

// Add helper function for point colors
const getPointColor = (points) => {
  if (points >= 0 && points <= 3) return 'red';
  if (points >= 4 && points <= 6) return 'yellow';
  if (points >= 7 && points <= 10) return 'green';
  return 'gray';
};

const cleanString = (value) => {
  // Check if value is a string
  if (typeof value === 'string') {
    // Remove brackets, quotes and extra spaces
    return value.replace(/[\[\]"']/g, '').trim();
  } 
  // If it's an array, join it
  else if (Array.isArray(value)) {
    return value.join(',').trim();
  }
  // Return empty string for null/undefined or convert to string for other types
  return value ? String(value).trim() : '';
};

const QuestionsList = ({ 
  questions, 
  answers, 
  handleAnswerChange, 
  setAnswers, 
  selectedSection,
  handleSubmit,
  isViewOnly = false
}) => {
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [mediaFiles, setMediaFiles] = useState({});  // Change to object to track per question

  const handleMediaClick = (e, questionId) => {
    e.stopPropagation();
    setSelectedQuestionId(questionId);
    setShowMediaModal(true);
  };

  const handleCapture = (questionId, fileData) => {
    const currentAnswer = answers[questionId] || {};
    // Replace existing media file instead of adding to array
    handleAnswerChange(questionId, {
      ...currentAnswer,
      mediaFiles: [
        {
          file: fileData.file,
          name: fileData.file.name,
          isNew: true
        }
      ]
    });
  };

  const handleRemove = (questionId, index) => {
    const currentAnswer = answers[questionId] || {};
    const removedFile = currentAnswer.mediaFiles?.[index];
    
    handleAnswerChange(questionId, {
      ...currentAnswer,
      mediaFiles: [], // Clear the array since we only allow one file
      deletedFiles: [
        ...(currentAnswer.deletedFiles || []),
        removedFile?.filename || removedFile?.name
      ].filter(Boolean)
    });
  };

  const calculatePoints = (question, selectedAnswer) => {
    if (!question.optionPoints) return 0;
    
    const points = cleanString(question.optionPoints).split(',').map(p => Number(p.trim()));
    const options = cleanString(question.answerOptions).split(',').map(opt => opt.trim());
    
    if (Array.isArray(selectedAnswer)) {
      return selectedAnswer.reduce((total, answer) => {
        const index = options.indexOf(answer);
        return total + (index !== -1 ? points[index] : 0);
      }, 0);
    }
    
    const index = options.indexOf(selectedAnswer);
    return index !== -1 ? points[index] : 0;
  };

  const handleAnswer = (questionId, changes) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...changes
      }
    }));
  };

  // Helper function to check if an answer has content
  const hasAnswer = (answer) => {
    return answer && (
      (typeof answer === 'string' && answer.trim() !== '') ||
      (Array.isArray(answer) && answer.length > 0)
    );
  };

  const renderAnswerInput = (question) => {
    const questionKey = question.id;
    const currentAnswer = answers[questionKey] || {};
    
    const cleanedOptions = cleanString(question.answerOptions).split(',').map(opt => opt.trim());

    switch (question.answerType) {
      case 'SingleOption':
        if (isViewOnly) {
          // View-only mode: Show selected answer with visual indication
          return (
            <div className="mt-3">
              <div className="selected-answer-display">
                <strong>Select Answer:</strong>
                <div className="selected-options mt-2">
                  {cleanedOptions.map((option, idx) => {
                    const isSelected = currentAnswer.answer === option;
                    return (
                      <div key={idx} className={`option-display ${isSelected ? 'selected' : 'unselected'}`}>
                        <i className={`fas ${isSelected ? 'fa-dot-circle' : 'fa-circle'} me-2`}></i>
                        {option}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="mt-3">
            {cleanedOptions.map((option, idx) => (
              <Form.Check
                key={idx}
                type="radio"
                name={`question_${questionKey}`}
                label={option}
                value={option}
                checked={currentAnswer.answer === option}
                disabled={isViewOnly}
                onChange={(e) => {
                  if (!isViewOnly) {
                    const points = calculatePoints(question, e.target.value);
                    handleAnswer(questionKey, {
                      answer: e.target.value,
                      points
                    });
                  }
                }}
                className="mb-2"
              />
            ))}
          </div>
        );

      case 'MultipleOptions':
        if (isViewOnly) {
          // View-only mode: Show selected answers with visual indication
          const selectedAnswers = typeof currentAnswer.answer === 'string' 
            ? currentAnswer.answer.split(', ').filter(a => a.trim() !== '')
            : Array.isArray(currentAnswer.answer) ? currentAnswer.answer : [];
            
          return (
            <div className="mt-3">
              <div className="selected-answer-display">
                <strong>Selected Answers:</strong>
                <div className="selected-options mt-2">
                  {cleanedOptions.map((option, idx) => {
                    const isSelected = selectedAnswers.includes(option);
                    return (
                      <div key={idx} className={`option-display ${isSelected ? 'selected' : 'unselected'}`}>
                        <i className={`fas ${isSelected ? 'fa-check-square' : 'fa-square'} me-2`}></i>
                        {option}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="mt-3">
            {cleanedOptions.map((option, idx) => {
              const isSelected = typeof currentAnswer.answer === 'string' 
                ? currentAnswer.answer.split(', ').includes(option)
                : Array.isArray(currentAnswer.answer) && currentAnswer.answer.includes(option);
                
              return (
                <Form.Check
                  key={idx}
                  type="checkbox"
                  label={option}
                  checked={isSelected}
                  disabled={isViewOnly}
                  onChange={(e) => {
                    if (!isViewOnly) {
                      let currentSelections = [];
                      if (typeof currentAnswer.answer === 'string' && currentAnswer.answer) {
                        currentSelections = currentAnswer.answer.split(', ');
                      } else if (Array.isArray(currentAnswer.answer)) {
                        currentSelections = [...currentAnswer.answer];
                      }
                      
                      if (e.target.checked) {
                        if (!currentSelections.includes(option)) {
                          currentSelections.push(option);
                        }
                      } else {
                        const index = currentSelections.indexOf(option);
                        if (index > -1) currentSelections.splice(index, 1);
                      }
                      
                      const answerString = currentSelections.join(', ');
                      const points = calculatePoints(question, currentSelections);
                      handleAnswer(questionKey, {
                        answer: answerString,
                        points
                      });
                    }
                  }}
                  className="mb-2"
                />
              );
            })}
          </div>
        );
      case 'Dropdown':
        if (isViewOnly) {
          // View-only mode: Show selected value
          return (
            <div className="mt-3">
              <div className="selected-answer-display">
                <strong>Select Answer:</strong>
                <div className="selected-value mt-2 p-2 bg-light rounded">
                  {currentAnswer.answer || <em className="text-muted">No answer selected</em>}
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <Form.Select
            className="mt-3"
            value={currentAnswer.answer || ''}
            disabled={isViewOnly}
            onChange={(e) => {
              if (!isViewOnly) {
                const points = calculatePoints(question, e.target.value);
                handleAnswer(questionKey, {
                  answer: e.target.value,
                  points
                });
              }
            }}
          >
            <option value="">Select an answer</option>
            {cleanedOptions.map((option, idx) => {
              const optionPoints = question.optionPoints ? 
                cleanString(question.optionPoints).split(',').map(p => Number(p.trim()))[idx] || 0 : 0;
              const pointColor = getPointColor(optionPoints);
              return (
                <option key={idx} value={option} className={`point-${pointColor}`}>
                  {option} (Points: {optionPoints})
                </option>
              );
            })}
          </Form.Select>
        );

      default: // FreeText
        if (isViewOnly) {
          // View-only mode: Show the text answer
          return (
            <div className="mt-3">
              <div className="selected-answer-display">
                <strong>Answer:</strong>
                <div className="selected-value mt-2 p-2 bg-light rounded">
                  {currentAnswer.answer || <em className="text-muted">No answer provided</em>}
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="mt-3">
            <Form.Control
              type="text"
              placeholder="Enter your answer"
              value={currentAnswer.answer || ''}
              disabled={isViewOnly}
              onChange={(e) => {
                if (!isViewOnly) {
                  handleAnswer(questionKey, {
                    answer: e.target.value
                  });
                }
              }}
            />
            <div className="d-flex align-items-center mt-2">
              <Form.Label className="mb-0 me-2"><strong>Points:</strong></Form.Label>
              <Form.Select
                style={{ maxWidth: '100px' }}
                value={currentAnswer.points || '0'}
                disabled={isViewOnly}
                className={`point-select point-${getPointColor(currentAnswer.points || 0)}`}
                onChange={(e) => {
                  if (!isViewOnly) {
                    handleAnswer(questionKey, {
                      ...currentAnswer,
                      points: parseInt(e.target.value)
                    });
                  }
                }}
              >
                {[...Array(11)].map((_, i) => {
                  const pointColor = getPointColor(i);
                  return (
                    <option key={i} value={i} className={`point-${pointColor}`}>
                      {i}
                    </option>
                  );
                })}
              </Form.Select>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="questions-list">
      {questions.map((question, index) => (
        <Card key={question.id} className="mb-3 shadow-sm">
          <Card.Header 
            onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
            className="cursor-pointer d-flex flex-column flex-md-row"
          >
            <Row className="w-100 align-items-center">
              <Col xs={12} md={9} className="d-flex align-items-center mb-2 mb-md-0">
                <div className={`question-number me-2 me-sm-3 ${answers[question.id]?.answer ? 'answered' : ''}`}>
                  {index + 1}
                </div>
                <h6 className="mb-0 text-break question-text">{question.questionText}</h6>
              </Col>
              <Col xs={12} md={3} className="d-flex justify-content-between justify-content-md-end align-items-center">
                <div className="d-flex align-items-center gap-2">
                  {answers[question.id]?.answer && (
                    <Badge bg="light" text="dark" className="points-badge">
                      Points: {answers[question.id]?.points || 0}
                    </Badge>
                  )}
                  {answers[question.id]?.mediaFiles?.length > 0 && (
                    <Badge bg="light" text="dark" className="media-badge">
                      <i className="fas fa-paperclip me-1" />
                      1
                    </Badge>
                  )}
                  <i className={`fas fa-chevron-${expandedQuestion === question.id ? 'up' : 'down'} ms-2`} />
                </div>
              </Col>
            </Row>
          </Card.Header>

          {expandedQuestion === question.id && (
            <Card.Body>
              {renderAnswerInput(question)}

              <div className="points-display mt-3 p-3 bg-light rounded">
                <Row className="align-items-center">
                  <Col xs={12} md={6}>
                    <div className="points-info">
                      <span className="points-label">Current Points:</span>
                      <span className="points-value ms-2 fw-bold">
                        {answers[question.id]?.points || 0}
                      </span>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Remarks section */}
              <Form.Group className="mt-3">
                <Form.Label>Remarks</Form.Label>
                {isViewOnly ? (
                  <div className="remarks-display p-2 bg-light rounded">
                    {answers[question.id]?.remarks || <em className="text-muted">No remarks provided</em>}
                  </div>
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Add remarks"
                    value={answers[question.id]?.remarks || ''}
                    disabled={isViewOnly}
                    onChange={(e) => {
                      if (!isViewOnly) {
                        handleAnswer(question.id, {
                          ...answers[question.id],
                          remarks: e.target.value
                        });
                      }
                    }}
                  />
                )}
              </Form.Group>

              {/* CAP section - only show if points < 10 */}
              {answers[question.id]?.points < 10 && (
                <Form.Group className="mt-3">
                  <Row className="align-items-center">
                    <Col xs={4} sm={3} md={2}>
                      <Form.Label className="mb-0 fw-bold">CAP:</Form.Label>
                    </Col>
                    <Col xs={8} sm={6} md={4} lg={3}>
                      {isViewOnly ? (
                        <div className="cap-display">
                          <Badge bg={answers[question.id]?.cap === 'Yes' ? 'success' : 'secondary'}>
                            {answers[question.id]?.cap || 'No'}
                          </Badge>
                        </div>
                      ) : (
                        <Form.Select
                          value={answers[question.id]?.cap || 'No'}
                          disabled={isViewOnly}
                          onChange={(e) => {
                            if (!isViewOnly) {
                              handleAnswer(question.id, {
                                ...answers[question.id],
                                cap: e.target.value
                              });
                            }
                          }}
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </Form.Select>
                      )}
                    </Col>
                  </Row>
                </Form.Group>
              )}

              {/* Media section - hide upload controls in view-only mode */}
              {!isViewOnly && (
                <div className="mt-4">
                  <h6 className="mb-3">Upload Media</h6>
                  <MediaCapture 
                    questionId={question.id}
                    isViewOnly={isViewOnly}
                    hasMedia={answers[question.id]?.mediaFiles?.length > 0}
                    onCapture={(file, fileName) => {
                      if (!isViewOnly) {
                        handleCapture(question.id, {
                          file,
                          fileName,
                          timestamp: Date.now()
                        });
                      }
                    }}
                  />
                </div>
              )}

              {/* Media preview - always show if there are files */}
              {answers[question.id]?.mediaFiles?.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-2">{isViewOnly ? 'Attached Media' : 'Media File'}</h6>
                  <MediaPreview 
                    mediaFiles={answers[question.id].mediaFiles}
                    baseUrl={`${BASE_URL}/uploads/audit-media`}
                    onRemove={isViewOnly ? undefined : (index) => handleRemove(question.id, index)}
                    allowRemove={!isViewOnly}
                    isViewOnly={isViewOnly}
                  />
                </div>
              )}
            </Card.Body>
          )}
        </Card>
      ))}

      {/* Hide modal in view-only mode */}
      {!isViewOnly && (
        <MediaModal 
          show={showMediaModal} 
          onHide={() => setShowMediaModal(false)} 
          onCapture={(file, fileName) => {
            handleCapture(selectedQuestionId, {
              file,
              fileName,
              timestamp: Date.now()
            });
            setShowMediaModal(false);
          }}
          questionId={selectedQuestionId} 
        />
      )}

      <style>
        {`
          .questions-list {
            max-width: 100%;
          }
          .cursor-pointer {
            cursor: pointer;
          }
          .question-number {
            min-width: 32px;
            height: 32px;
            border-radius: 50%;
            background-color: var(--bs-gray-200);
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            flex-shrink: 0;
          }
          .question-number.answered {
            background-color: var(--bs-success);
            color: white;
          }
          .question-text {
            font-size: 0.95rem;
            line-height: 1.4;
          }
          .points-badge, .media-badge {
            font-size: 0.75rem;
            padding: 0.35rem 0.7rem;
            white-space: nowrap;
          }
          .points-display {
            border-left: 4px solid var(--bs-primary);
          }
          .points-info {
            font-size: 0.875rem;
            display: flex;
            align-items: center;
          }
          .points-label {
            color: var(--bs-gray-700);
          }
          .points-value {
            font-size: 1rem;
            color: var(--bs-primary);
          }
          
          /* Point color styles */
          .point-select.point-red {
            border-color: #dc3545;
            background-color: #fff5f5;
          }
          .point-select.point-yellow {
            border-color: #ffc107;
            background-color: #fffbf0;
          }
          .point-select.point-green {
            border-color: #198754;
            background-color: #f0fff4;
          }
          
          .point-red {
            color: #dc3545 !important;
            font-weight: 600;
          }
          .point-yellow {
            color: #e67e22 !important;
            font-weight: 600;
          }
          .point-green {
            color: #198754 !important;
            font-weight: 600;
          }
          
          /* Dropdown option styling */
          select option.point-red {
            background-color: #fff5f5;
            color: #dc3545;
          }
          select option.point-yellow {
            background-color: #fffbf0;
            color: #e67e22;
          }
          select option.point-green {
            background-color: #f0fff4;
            color: #198754;
          }
          
          /* View-only mode styles */
          .selected-answer-display {
            background-color: #f8f9fa;
            padding: 1rem;
            border-radius: 8px;
            border-left: 4px solid #0d6efd;
          }
          
          .selected-options {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .option-display {
            padding: 0.5rem;
            border-radius: 6px;
            display: flex;
            align-items: center;
            transition: all 0.2s;
          }
          
          .option-display.selected {
            background-color: #d1edff;
            border: 1px solid #0d6efd;
            font-weight: 500;
          }
          
          .option-display.unselected {
            background-color: #ffffff;
            border: 1px solid #dee2e6;
            color: #6c757d;
          }
          
          .option-display.selected i {
            color: #0d6efd;
          }
          
          .option-display.unselected i {
            color: #adb5bd;
          }
          
          .selected-value {
            font-size: 0.95rem;
            line-height: 1.4;
            min-height: 2.5rem;
            display: flex;
            align-items: center;
          }
          
          .remarks-display {
            min-height: 2.5rem;
            font-size: 0.95rem;
            line-height: 1.4;
            display: flex;
            align-items: flex-start;
            padding-top: 0.75rem !important;
          }
          
          .cap-display {
            display: flex;
            align-items: center;
            height: 38px;
          }
          
          .media-capture-container {
            border: 1px dashed var(--bs-gray-400);
            border-radius: 8px;
            padding: 1.5rem;
            background-color: var(--bs-gray-100);
            text-align: center;
          }
          .media-preview {
            width: 100%;
            border-radius: 8px;
            overflow: hidden;
          }
          .media-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
            gap: 1rem;
          }
          .media-item {
            position: relative;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            aspect-ratio: 1;
          }
          .media-item img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .remove-btn {
            position: absolute;
            top: 5px;
            right: 5px;
            background-color: rgba(255,255,255,0.9);
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            cursor: pointer;
            transition: all 0.2s;
          }
          .remove-btn:hover {
            background-color: var(--bs-danger);
            color: white;
          }
          @media (max-width: 768px) {
            .media-grid {
              grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
            }
            .question-text {
              font-size: 0.9rem;
            }
          }
          @media (max-width: 576px) {
            .media-grid {
              grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
              gap: 0.75rem;
            }
            .question-text {
              font-size: 0.85rem;
            }
            .question-number {
              min-width: 28px;
              height: 28px;
              font-size: 0.85rem;
            }
            .card-header {
              padding: 0.75rem;
            }
            .card-body {
              padding: 1rem;
            }
            .selected-options {
              gap: 0.25rem;
            }
            .option-display {
              padding: 0.4rem;
              font-size: 0.9rem;
            }
          }
        `}
      </style>
    </div>
  );
};

export default QuestionsList;